import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "core/src/store/store.types";
import { LoadingStatusEnum } from "etl/types";

const getFormStepState = (state: RootState) => state.formStep;

const getImportLoadingStatus = createSelector(getFormStepState, (state) => state.importLoadingStatus);
const getImportIsLoading = createSelector(getFormStepState, (state) => state.importLoadingStatus === LoadingStatusEnum.Loading);

const getValidatingSelectImportStep = createSelector(getFormStepState, (state) => state.validatingSelectImportStep);

const getIsRefreshingBrand = createSelector(getFormStepState, (state) => state.isRefreshingBrand);
const getIsRefreshingSupplier = createSelector(getFormStepState, (state) => state.isRefreshingSupplier);
const getIsRefreshingCategory = createSelector(getFormStepState, (state) => state.isRefreshingCategory);
const getIsRefreshingUser = createSelector(getFormStepState, (state) => state.isRefreshingUser);
const getIsRefreshingScheduleType = createSelector(getFormStepState, (state) => state.isRefreshingScheduleType);
const getIsRefreshingProduct = createSelector(getFormStepState, (state) => state.isRefreshingProduct);
const getIsRefreshingLaboratory = createSelector(getFormStepState, (state) => state.isRefreshingLaboratory);

const getDictionnaryProductTriggered = createSelector(getFormStepState, (state) => state.dictionnaryProductTriggered);
const getDictionnaryLaboratoryTriggered = createSelector(getFormStepState, (state) => state.dictionnaryLaboratoryTriggered);

const getOutputGenerationTriggered = createSelector(getFormStepState, (state) => state.outputGenerationTriggered);

const getUploadingFile = createSelector(getFormStepState, (state) => state.uploadingFile);

// pre-processing
const getUnzipRunning = createSelector(getFormStepState, (state) => state.unzipRunning);
const getPreProcessRunning = createSelector(getFormStepState, (state) => state.preProcessRunning);
const getInspectProcessFileRunning = createSelector(getFormStepState, (state) => state.inspectProcessFileRunning);
const getListeningToMinificationEnding = createSelector(getFormStepState, (state) => state.listeningToMinificationEnding);

// Upload to S3 Bucket
const getUploadingS3Bucket = createSelector(getFormStepState, (state) => state.uploadingS3Bucket);
const getListeningToUploadS3BucketEnding = createSelector(getFormStepState, (state) => state.listeningToUploadS3BucketEnding);

const getImportPdfRunning = createSelector(getFormStepState, (state) => state.importPdfRunning);

const getCorrectingCSvLineManually = createSelector(getFormStepState, (state) => state.correctingCSvLineManually);
const getReuploadingCsv = createSelector(getFormStepState, (state) => state.reuploadingCsv);

export const useFormStepSelectors = () => {
	const importLoadingStatus = useSelector(getImportLoadingStatus);
	const importIsLoading = useSelector(getImportIsLoading);

	const validatingSelectImportStep = useSelector(getValidatingSelectImportStep);

	const isRefreshingBrand = useSelector(getIsRefreshingBrand);
	const isRefreshingSupplier = useSelector(getIsRefreshingSupplier);
	const isRefreshingCategory = useSelector(getIsRefreshingCategory);
	const isRefreshingUser = useSelector(getIsRefreshingUser);
	const isRefreshingScheduleType = useSelector(getIsRefreshingScheduleType);
	const isRefreshingProduct = useSelector(getIsRefreshingProduct);
	const isRefreshingLaboratory = useSelector(getIsRefreshingLaboratory);
	const dictionnaryProductTriggered = useSelector(getDictionnaryProductTriggered);
	const dictionnaryLaboratoryTriggered = useSelector(getDictionnaryLaboratoryTriggered);

	const outputGenerationTriggered = useSelector(getOutputGenerationTriggered);

	const uploadingFile = useSelector(getUploadingFile);

	// pre-processing
	const unzipRunning = useSelector(getUnzipRunning);
	const preProcessRunning = useSelector(getPreProcessRunning);
	const inspectProcessFileRunning = useSelector(getInspectProcessFileRunning);
	const listeningToMinificationEnding = useSelector(getListeningToMinificationEnding);

	// Upload to S3 Bucket
	const uploadingS3Bucket = useSelector(getUploadingS3Bucket);
	const listeningToUploadS3BucketEnding = useSelector(getListeningToUploadS3BucketEnding);

	const importPdfRunning = useSelector(getImportPdfRunning);

	const correctingCSvLineManually = useSelector(getCorrectingCSvLineManually);
	const reuploadingCsv = useSelector(getReuploadingCsv);

	return {
		// common
		importLoadingStatus,
		importIsLoading,
		// import
		validatingSelectImportStep,
		// brand
		isRefreshingBrand,
		// supplier
		isRefreshingSupplier,
		// category
		isRefreshingCategory,
		// product
		dictionnaryProductTriggered,
		// laboratory
		dictionnaryLaboratoryTriggered,
		isRefreshingLaboratory,
		// product
		isRefreshingProduct,
		// schedule type
		isRefreshingScheduleType,
		// user
		isRefreshingUser,
		// generate ouput
		outputGenerationTriggered,
		// uploading file
		uploadingFile,
		// pre-processing
		unzipRunning,
		preProcessRunning,
		inspectProcessFileRunning,
		listeningToMinificationEnding,
		// uploading s3 bucket
		uploadingS3Bucket,
		listeningToUploadS3BucketEnding,
		// import PDF
		importPdfRunning,
		// correcting CSV line manually
		correctingCSvLineManually,
		// reupload CSV
		reuploadingCsv
	}
}

export type UseFormStepSelectors = ReturnType<typeof useFormStepSelectors>;
